<template>
  <Modal
    :show="show.hrisProfileLink"
    width="70%"
    min-height="60%"
    hide-default-actions
    content-class="hris-profile-link-modal-content"
    @close="close"
  >
    <template #title>
      Link to Workday profile
    </template>

    <template #content>
      <div class="d-flex ga-6 h-100">
        <v-sheet
          class="d-flex flex-column"
          width="320"
        >
          <p class="text-body-3 text-textLight mb-3">
            Select a matching Workday profile to link this draft to.
          </p>

          <div class="candidates-list d-flex flex-column ga-2 flex-grow-1 overflow-y-auto">
            <v-card
              v-for="item in candidates"
              :key="item.ulid"
              variant="outlined"
              rounded="lg"
              class="pa-3 candidate-row cursor-pointer"
              :class="{ selected: selectedUlid === item.ulid }"
              @click="selectCandidate(item)"
            >
              <p class="text-body-2 font-weight-medium text-textDarkest mb-1">
                {{ item.title }}
              </p>
              <div class="d-flex align-center ga-3">
                <JobProfileStatus
                  :is-hris-profile="true"
                  :status="item.status"
                />
                <JobProfileMeta :profile="item" />
              </div>
            </v-card>
          </div>

          <div class="mt-4">
            <v-btn
              variant="flat"
              color="primary"
              rounded="lg"
              :disabled="!selectedUlid"
              :loading="modalLoading"
              @click="link"
            >
              Link to this Workday profile
            </v-btn>
          </div>
        </v-sheet>

        <v-divider vertical />

        <div class="modal-right d-flex flex-column">
          <div
            v-if="previewLoading"
            class="d-flex justify-center align-center flex-grow-1"
          >
            <v-progress-circular indeterminate color="primary" />
          </div>

          <template v-else-if="previewProfile">
            <p class="text-body-2 font-weight-medium text-textDarkest mb-3">
              {{ previewProfile.title }}
            </p>
            <div class="preview-fields overflow-y-auto flex-grow-1">
              <div
                v-for="field in previewFields"
                :key="field.type"
                class="mb-4"
              >
                <div class="d-flex justify-end mt-2 mb-1">
                  <FieldLabel class="mb-1 ">
                    {{ field.label }}
                  </FieldLabel>
                </div>
                <DocumentPreviewFieldHtml :html="field.headerHtml" />
                <DocumentPreviewFieldHtml :html="field.html" />
                <DocumentPreviewFieldHtml :html="field.footerHtml" />
              </div>
            </div>
            <div v-if="previewProfile?.draft_profiles?.length > 0">
              <v-alert
                type="warning"
                variant="tonal"
                rounded="lg"
                height="60"
                class="mt-3"
              >
                <strong>{{ previewProfile.title }}</strong> profile already has
                {{
                  previewProfile.draft_profiles.length === 1
                    ? 'a draft'
                    : `${previewProfile.draft_profiles.length} drafts`
                }}
                linked to it. Linking another draft may cause confusion.
              </v-alert>
            </div>
          </template>

          <div
            v-else
            class="d-flex justify-center align-center flex-grow-1"
          >
            <p class="text-body-3 text-textLight">
              Select a profile on the left to preview it.
            </p>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import { useAppLoading } from '@/stores/app/loading';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useModals } from '@/stores/modals/modals';
import { getJobProfileHris } from '@/api/jobProfile';
import Modal from '@/components/shared/Modal.vue';
import JobProfileStatus from '@/components/jobProfiles/editor/JobProfileStatus.vue';
import JobProfileMeta from '@/components/jobProfiles/editor/JobProfileMeta.vue';
import FieldLabel from '@/components/editor/field/FieldLabel.vue';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';

const currentJobProfile = useCurrentJobProfile();
const { jobProfile } = storeToRefs(currentJobProfile);
const { show } = storeToRefs(useModals());
const { setModal } = useModals();
const { modalLoading } = storeToRefs(useAppLoading());

const candidates = computed(() => jobProfile.value?.hris_matching ?? []);

const selectedUlid = ref(null);
const previewProfile = ref(null);
const previewLoading = ref(false);

const previewFields = computed(() => {
  if (!previewProfile.value?.fields) return [];
  return previewProfile.value.fields
    .map((f) => ({
      ...f,
      headerHtml: f.header?.map((b) => b.html).join('') ?? '',
      html: f.blocks.map((b) => b.html).join('') ?? '',
      footerHtml: f.footer?.map((b) => b.html).join('') ?? '',
    }))
    .filter((f) => !isEmptyHtml(f.headerHtml + f.html + f.footerHtml));
});

const selectCandidate = async (item) => {
  selectedUlid.value = item.ulid;
  previewProfile.value = null;
  previewLoading.value = true;
  try {
    previewProfile.value = await getJobProfileHris({ ulid: item.ulid, rescore: true });
  } finally {
    previewLoading.value = false;
  }
};

const reset = () => {
  selectedUlid.value = null;
  previewProfile.value = null;
  previewLoading.value = false;
};

const close = () => {
  reset();
  setModal({ field: 'hrisProfileLink', value: false });
};

const link = async () => {
  if (!selectedUlid.value || !jobProfile.value?.ulid) return;
  await currentJobProfile.linkHrisProfile(selectedUlid.value);
  close();
};

watch(candidates, (list) => {
  if (list.length === 1 && selectedUlid.value !== list[0].ulid) {
    selectCandidate(list[0]);
  }
}, { immediate: true });
</script>

<style lang="less" scoped>
.modal-right {
  flex: 1;
  min-width: 0;
}

.candidate-row {
  border-color: @stroke-default;
  transition: border-color 0.15s;

  &.selected {
    border-color: @brand-primary;
  }
}
</style>

<style lang="less">
.hris-profile-link-modal-content {
  & > .v-sheet {
    display: flex;
    flex-direction: column;
  }
  .modal-content-wrapper {
    flex-grow: 1;
    display: flex;
    .content {
      flex-grow: 1;
    }
  }
}
</style>
